import { makeAutoObservable, runInAction } from 'mobx'
import { getUrlEndPath } from 'shared/lib'
import { type IDropdownItem } from 'shared/ui'
import { IntegrationKey, type Integration } from 'entities/Integrations'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type ChatbotBooking, type IBookingType, type IChatbotField } from 'entities/Chatbot'
import { ManageIntegrationConnectionStore } from 'features/ManageIntegrationConnection'
import { MeetingsStore } from 'widgets/Meetings/store/meetingsStore'
import type { ICalendlyIntegrationMeetingCustom } from 'pages/chatbot/pages/agentBookAppointment/pages/connectCalendar/store/types'
import type { IEventLinkDropdownItem } from 'pages/chatbot/pages/agentBookAppointment/pages/connectCalendar/store/connectCalendar'
import { CalendlyFieldsStore } from 'pages/chatbot/store/calendlyFieldsStore'
import { buildSectionInstruction } from 'pages/chatbot/ui/UnifiedAgent/lib/buildSectionInstruction'

type IBookingOrigin = {
  bookingEnabled: boolean
  type: IBookingType
  link: ChatbotBooking['link']
  smartBookingEnabled: boolean
}

export class BookingSectionStore {
  isCalendlyConnected = false
  isLoading = false
  connectionError = ''
  eventLinkError = ''
  bookingFields: IChatbotField[] = []
  origin: IBookingOrigin | null = null
  private _integrationMap: Map<number | string, Integration> = new Map()
  private _manageIntegrationConnectionStore = new ManageIntegrationConnectionStore()
  private _meetingsStore: MeetingsStore
  private _calendlyFieldsStore: CalendlyFieldsStore

  constructor(readonly entities: IEntitiesStore, private _booking: ChatbotBooking) {
    this._meetingsStore = new MeetingsStore({ contact: null }, this.entities)
    this._calendlyFieldsStore = new CalendlyFieldsStore(this.entities)
    makeAutoObservable(this)
    this.init()
  }

  get calendlyFieldsStore() {
    return this._calendlyFieldsStore
  }

  get bookingEnabled() {
    return this._booking.bookingEnabled
  }

  get bookingType() {
    return this._booking.type
  }

  get smartBookingEnabled() {
    return this._booking.smartBookingEnabled
  }

  get eventLink(): IEventLinkDropdownItem | null {
    const link = this._booking.link
    if (!link) return null
    return {
      id: link.id,
      label: link.name,
      labelRight: getUrlEndPath(link.value),
      labelRightVariant: 'body-md-regular',
      data: {
        id: link.id,
        name: link.name,
        scheduling_url: link.value,
      },
    }
  }

  get isMeetingLoading() {
    return this._meetingsStore.loading
  }

  get search() {
    return this._meetingsStore.search
  }

  get connectionList(): IDropdownItem[] {
    const list: IDropdownItem[] = []

    this.entities.integrationsStore
      .getIntegrations([IntegrationKey.calendly, IntegrationKey.hubspot])
      .forEach((item) => {
        this._integrationMap.set(item.id, item)

        if (item.key === IntegrationKey.calendly) {
          if (item.connected) {
            this.setIsCalendlyConnected(true)
            this._meetingsStore.onSelectedIntegration(item)
          }
          list.push({
            id: item.id,
            iconL: 'calendly',
            label: 'Calendly',
            labelRight: item.connected ? null : 'Connect',
            labelRightVariant: 'body-md-regular',
          })
        }

        if (item.key === IntegrationKey.hubspot) {
          list.push({
            id: item.id,
            iconL: 'hubspot',
            label: 'HubSpot',
            labelRight: 'Coming soon',
            labelRightVariant: 'body-md-regular',
            disabled: true,
          })
        }
      })

    return list
  }

  get meetingsList(): IDropdownItem[] {
    return (
      this._meetingsStore.filteredMeetings?.map((meeting) => {
        const calendarEvent: ICalendlyIntegrationMeetingCustom = {
          name: meeting.name,
          scheduling_url: 'scheduling_url' in meeting ? meeting.scheduling_url : '',
          id: meeting.id,
        }
        const isConfigurationValid =
          'is_configuration_valid' in meeting ? meeting?.is_configuration_valid : false

        return {
          id: meeting.id,
          label: calendarEvent.name,
          labelRight: getUrlEndPath(calendarEvent.scheduling_url),
          labelRightVariant: 'body-md-regular',
          data: calendarEvent,
          ...(!isConfigurationValid && {
            disabled: true,
            tooltipProps: {
              label: (
                <>
                  This link is missing required fields in Calendly.
                  <br />
                  Update it in Calendly to use.
                </>
              ),
              placement: 'top',
            },
          }),
        }
      }) ?? []
    )
  }

  get sectionInstruction() {
    if (!this.origin) return ''
    return buildSectionInstruction('Booking', this.origin.bookingEnabled, this.bookingEnabled)
  }

  get hasChanges() {
    if (!this.origin) return false
    return (
      this.bookingEnabled !== this.origin.bookingEnabled ||
      this.bookingType !== this.origin.type ||
      this.smartBookingEnabled !== this.origin.smartBookingEnabled ||
      this.eventLink?.id !== this.origin.link?.id
    )
  }

  init = () => {
    this.origin = {
      bookingEnabled: this.bookingEnabled,
      type: this.bookingType,
      link: this._booking.link,
      smartBookingEnabled: this.smartBookingEnabled,
    }
  }

  enhancePayload = (isEdit: boolean) => {
    const origin = !isEdit && this.origin
    return {
      book_appointment_enabled: origin ? origin.bookingEnabled : this.bookingEnabled,
      book_appointment_type: origin ? origin.type : this.bookingType,
    }
  }

  setBookingType = (type: IBookingType) => {
    this._booking.setType(type)
  }

  setSmartBookingEnabled = (value: boolean) => {
    this._booking.setSmartBookingEnabled(value)
  }

  setConnection = async (connection: IDropdownItem) => {
    if (this.isCalendlyConnected) return
    const integration = this._integrationMap.get(connection.id)
    if (!integration) return

    runInAction(() => {
      this.isLoading = true
    })
    try {
      await this._manageIntegrationConnectionStore.connect(integration)
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  setIsCalendlyConnected = (value: boolean) => {
    this.isCalendlyConnected = value
    if (value) this.connectionError = ''
  }

  setLink = (link: IDropdownItem) => {
    const item = link as IEventLinkDropdownItem

    this._booking.setLink({
      value: item.data.scheduling_url,
      id: String(item.data.id),
      name: item.data.name,
    })

    this.eventLinkError = ''
    void this._loadCalendlyFields(String(item.id))
  }

  setSearch = (value: string) => {
    this._meetingsStore.setSearch(value)
  }

  private _loadCalendlyFields = async (linkId: string) => {
    await this._calendlyFieldsStore.fetchFields({ params: linkId })
    runInAction(() => {
      this.bookingFields = this._calendlyFieldsStore.fields
    })
    this.entities.mergeFieldStore.addMergeFieldsIntegrations(
      IntegrationKey.calendly,
      this._calendlyFieldsStore.mergeFieldCalendly
    )
  }

  setBookingEnabled = (value: boolean) => {
    this._booking.setEnabled(value)

    if (!value) {
      this.bookingFields = []
      this.entities.mergeFieldStore.removeMergeFieldsIntegrations(IntegrationKey.calendly)
      this.connectionError = ''
      this.eventLinkError = ''
    }
  }

  private _setConnectionErrors = () => {
    if (!this.isCalendlyConnected) {
      this.connectionError = 'Select your booking app'
    } else {
      this.eventLinkError = 'Select an event to share with contacts'
    }
  }

  handleBookingLinkError = (error: string) => {
    if (error.endsWith('field is required.')) {
      this._setConnectionErrors()
    } else {
      this.connectionError = error
    }
  }

  resetError = (key: 'connectionError' | 'eventLinkError') => {
    this[key] = ''
  }

  validateSection = () => {
    if (!this.bookingEnabled) return true
    if (!this.isCalendlyConnected || !this.eventLink) {
      this._setConnectionErrors()
      return false
    }
    return true
  }

  resetSection = () => {
    if (!this.origin) return
    this.setBookingEnabled(this.origin.bookingEnabled)
    this.setBookingType(this.origin.type)
    this.setSmartBookingEnabled(this.origin.smartBookingEnabled)
    if (this.origin.link) {
      this._booking.setLink(this.origin.link)
      if (this.origin.bookingEnabled) {
        void this._loadCalendlyFields(this.origin.link.id)
      }
    }
  }

  dispose = () => {
    this.entities.mergeFieldStore.removeMergeFieldsIntegrations(IntegrationKey.calendly)
  }
}
